import { useDialog } from '@/components/Molecules/Dialog/Dialog';
import { ConfirmationDialog } from '@/components/Organisms/ConfirmationDialog/ConfirmationDialog';
import type { APIClientError } from '@/services/Http/ApiClientError';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import type { FormEvent, HTMLProps } from 'react';
import { useRef, useState } from 'react';
import type { FCC } from 'react-fc';
interface IProps extends HTMLProps<HTMLFormElement> {
  apiUrl: string;
  method?: 'post' | 'delete';
  values?: {
    [key: string]: string | number;
  };
  onSubmitStart?: () => boolean | void;
  onSubmitEnd?: (response: any) => void;
  onSubmitError?: (error: APIClientError) => void;
  withConfirmation?: boolean;
}
export const CsrfForm: FCC<IProps> = ({
  apiUrl,
  onSubmitStart,
  onSubmitEnd,
  onSubmitError,
  method,
  values = {},
  children,
  className,
  withConfirmation
}) => {
  const [requiresConfirmation, setRequiresConfirmation] = useState(withConfirmation);
  const confirmationDialog = useDialog({
    lazy: true
  });
  const ref = useRef<HTMLFormElement>(null);
  const doSubmit = async (event?: FormEvent<HTMLFormElement>) => {
    if (onSubmitStart) {
      const proceed = onSubmitStart();
      if (proceed === false) {
        event?.preventDefault();
        return;
      }
    }
    if (!onSubmitEnd) {
      // no client-side handling - use default server behaviour
      return;
    }
    event?.preventDefault();
    const {
      _url,
      _method,
      ...body
    } = Object.fromEntries(new FormData(ref.current).entries());
    try {
      const api = new BrowserHttpClient();
      const query = {
        path: _url.toString(),
        body
      };
      const response = await (_method === 'delete' ? api.delete(query) : api.post(query));
      onSubmitEnd(response);
    } catch (e) {
      console.warn(`[CSRF Frontend Error] ${e.message}`, {
        _url,
        _method,
        status: e?.response
      });
      onSubmitError?.(e);
    }
  };
  const onSubmit = async (event_0: FormEvent<HTMLFormElement>) => {
    if (requiresConfirmation) {
      event_0.stopPropagation();
      event_0.preventDefault();
      confirmationDialog.open();
      setRequiresConfirmation(false);
      return;
    }
    return doSubmit(event_0);
  };
  return <form ref={ref} method="post" action="/api/csrf" onSubmit={onSubmit} className={className}>
            {method && <input type="hidden" name="_method" value={method} />}
            <input type="hidden" name="_url" value={apiUrl} />
            {values && Object.keys(values).map(key => <input key={key} type="hidden" name={key} value={values[key].toString()} />)}
            {children}
            {withConfirmation && <ConfirmationDialog dialog={confirmationDialog} onConfirm={() => {
      ref?.current?.submit();
    }} onCancel={() => {
      setRequiresConfirmation(withConfirmation);
    }} />}
        </form>;
};